import type { PayloadAction } from '@reduxjs/toolkit'
import { createSelector, createSlice } from '@reduxjs/toolkit'
import type {
  CartItem,
  CartState,
  DiscountDTO,
  ICartItemSingle,
  Language,
  ProductLightDTO
} from 'ecosystem'
import {
  arrayFindAndReplaceItem,
  arrayFindAndUpdateItem,
  flatCartItems,
  getProductDiscountPrice,
  getProductPrice,
  isNullable,
  sumCart
} from 'shared-utils'
import { v4 as uuidv4 } from 'uuid'
import {
  deleteCartSliceFromStorage,
  getCartSliceFromStorage,
  updateCartSliceInStorage
} from './utils'

const initialState: CartState = {
  cartLocale: null,
  cartItems: [],
  cartTotal: 0,
  cartDiscountCode: null,
  cartStateUuid: null
}

const isDiscountApplicableToCart = (
  cartItems: CartItem[],
  discountCode: DiscountDTO | null
): boolean => {
  return (
    !!discountCode &&
    cartItems.some((cartItem) => {
      if (cartItem.type === 'SINGLE') {
        return !!getProductDiscountPrice(cartItem.meta.singleType.product, discountCode)
      }
      return cartItem.meta.groupType.items.some(
        (groupItem) => !!getProductDiscountPrice(groupItem.product, discountCode)
      )
    })
  )
}

const slice = createSlice({
  name: 'cart',
  initialState: () => {
    return getCartSliceFromStorage() ?? initialState
  },
  reducers: {
    setCartLocale: (state, { payload: cartLocale }: PayloadAction<Language>) => {
      state.cartLocale = cartLocale
      updateCartSliceInStorage(state)
      return state
    },

    // Add to cart or update existing cart item, used to update quantity as well for existing item
    addToCart: (state, { payload: cartItem }: PayloadAction<CartItem>) => {
      if (cartItem.type === 'SINGLE' && !getProductPrice(cartItem.meta.singleType.product)) {
        return state
      }

      const existingCartItem = state.cartItems.find(({ id }) => id === cartItem.id)

      if (!existingCartItem) {
        state.cartItems.push(cartItem)
      } else if (cartItem.type !== existingCartItem.type) {
        arrayFindAndReplaceItem({
          arr: state.cartItems,
          newItem: cartItem,
          compare: (arrItem) => arrItem.id === cartItem.id
        })
      } else if (cartItem.type === 'SINGLE' && existingCartItem.type === 'SINGLE') {
        arrayFindAndUpdateItem({
          arr: state.cartItems,
          compare: (item) => item.id === cartItem.id,
          cb: (foundItem) => {
            const singleFoundItem = foundItem as ICartItemSingle
            const singleCartItem = cartItem

            singleFoundItem.qty = singleCartItem.qty

            if (singleCartItem.offerInfo) {
              singleFoundItem.offerInfo = singleCartItem.offerInfo
            }

            /**
             * When we add a new cart item from an offer, the price is usually lower, and we want
             * to keep this price even when we click on add to cart in Product detail page for
             * the same product
             */
            const existingPrice = singleFoundItem.meta.singleType.product.price
            const newPrice = singleCartItem.meta.singleType.product.price

            if (!isNullable(existingPrice) && !isNullable(newPrice)) {
              singleFoundItem.meta.singleType.product.price = Math.min(existingPrice, newPrice)
            } else if (!isNullable(newPrice)) {
              singleFoundItem.meta.singleType.product.price = newPrice
            }
          }
        })
      } else {
        arrayFindAndReplaceItem({
          arr: state.cartItems,
          newItem: cartItem,
          compare: (arrItem) => arrItem.id === cartItem.id
        })
      }

      state.cartTotal = sumCart(state.cartItems)
      state.cartStateUuid = uuidv4()

      updateCartSliceInStorage(state)
      return state
    },

    removeFromCart: (state, { payload: cartItem }: PayloadAction<CartItem>) => {
      state.cartItems = state.cartItems.filter((e) => e.id !== cartItem.id)

      if (!isDiscountApplicableToCart(state.cartItems, state.cartDiscountCode)) {
        state.cartDiscountCode = null
      }

      state.cartTotal = sumCart(state.cartItems)
      state.cartStateUuid = uuidv4()

      updateCartSliceInStorage(state)
    },

    applyDiscountCode: (state, { payload: discountCode }: PayloadAction<DiscountDTO>) => {
      if (isDiscountApplicableToCart(state.cartItems, discountCode)) {
        state.cartDiscountCode = discountCode
        state.cartStateUuid = uuidv4()

        updateCartSliceInStorage(state)
      }
    },

    removeDiscountCode: (state) => {
      // Reset all prices to original and recalculate
      state.cartDiscountCode = null
      state.cartStateUuid = uuidv4()

      updateCartSliceInStorage(state)
    },

    cleanCart: () => {
      deleteCartSliceFromStorage()
      return initialState
    },

    // Best-effort price refresh: merge the latest gross + net prices for any
    // product the BE returned, by id. Products not in the payload (deleted /
    // errored / partial response) keep their existing snapshot — BE is the
    // final gatekeeper at order submit.
    refreshCartProducts: (state, { payload }: PayloadAction<ProductLightDTO[]>) => {
      if (!payload.length) return state

      const byId = new Map(payload.map((p) => [p.id, p]))
      const apply = (target: { id: string } & Partial<ProductLightDTO>) => {
        const fresh = byId.get(target.id)
        if (!fresh) return
        target.price = fresh.price
        target.originalPrice = fresh.originalPrice
        target.priceWithoutVat = fresh.priceWithoutVat
        target.originalPriceWithoutVat = fresh.originalPriceWithoutVat
        // The volume ladder is part of the price picture and moves with it: a
        // cart restored from storage would otherwise keep scoring against an
        // offer the merchant has since changed or withdrawn. `null` is a real
        // value here — it is how the BE says "this product has no ladder".
        target.quantityDiscount = fresh.quantityDiscount ?? null
      }

      state.cartItems.forEach((item) => {
        if (item.type === 'SINGLE') {
          apply(item.meta.singleType.product)
          item.meta.singleType.subItems?.forEach((sub) => apply(sub.product))
        } else {
          item.meta.groupType.items.forEach((detail) => {
            apply(detail.product)
            detail.subItems?.forEach((sub) => apply(sub.product))
          })
        }
      })

      state.cartTotal = sumCart(state.cartItems)
      state.cartStateUuid = uuidv4()
      updateCartSliceInStorage(state)
      return state
    }
  }
})

// available actions - add more as needed, first they will have to be declared above
export const {
  setCartLocale,
  addToCart,
  removeFromCart,
  applyDiscountCode,
  removeDiscountCode,
  cleanCart,
  refreshCartProducts
} = slice.actions

// available selectors - add more as needed
export const selectedCart = (state: { cart: CartState }) => state.cart
export const selectedCartItems = (state: { cart: CartState }) => state.cart.cartItems
export const selectedCartDiscount = (state: { cart: CartState }) => state.cart.cartDiscountCode
export const selectedCartTotal = (state: { cart: CartState }) => state.cart.cartTotal

// Selector to get cart items with current prices (already calculated)
export const selectedCartItemsWithPrices = (state: { cart: CartState }) => state.cart.cartItems

/**
 * How many units of each product the cart holds, keyed by product id.
 *
 * Built from `flatCartItems` rather than by matching cart-item ids, because a
 * product can be in the cart twice over: as its own `SINGLE` line (whose id *is*
 * the product id) and again inside a `GROUP` bundle (whose id is the bundle's).
 * Flattening counts both, at the same effective quantity the checkout prices —
 * so a product card and the eventual order line agree about how many the shopper
 * has.
 *
 * Memoised so the whole cart is walked once per change and every consumer does
 * an O(1) lookup. A product grid mounts one of these per card; without the
 * memo each card would re-walk the basket.
 */
export const selectedCartProductQuantities = createSelector(
  // Reads the slice defensively rather than through `selectedCartItems`: this is
  // the one cart selector reached from a store that may not register the cart
  // reducer at all. gs-sfa reuses `ProductListItem` for its settings preview,
  // and its store has no `cart` slice — without the guard the admin preview
  // throws on `state.cart.cartItems`.
  [(state: { cart?: CartState }) => state.cart?.cartItems],
  (cartItems) => {
    const quantities = new Map<string, number>()

    flatCartItems(cartItems ?? []).forEach(({ product, qty }) => {
      quantities.set(product.id, (quantities.get(product.id) ?? 0) + qty)
    })

    return quantities
  }
)

export default slice.reducer
